// src/services/users.service.ts - Update the getUsers function
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  DocumentData,
  limit,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import {
  CustomUserFields,
  ProfileVisibility,
  SubscriptionTier,
} from "@/types/user";
import { enableNetwork, disableNetwork } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const USERS_COLLECTION = "users";
const HANDLE_COLLECTION = "handles";

/**
 * Utility function to reconnect to Firestore when connection errors occur
 * @returns A promise that resolves when reconnection is complete
 */
export const reconnectFirestore = async (): Promise<void> => {
  try {
    // Only attempt reconnection if we appear to be online
    if (navigator.onLine) {
      // Disable network to reset connection
      await disableNetwork(db);

      // Give a short delay to ensure the connection is fully closed
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Re-enable network connection
      await enableNetwork(db);
    }
  } catch (error) {
    // Let the caller handle any errors
    throw error;
  }
};

/**
 * Creates a new user document in Firestore
 * @param userId - The user's UID
 * @param userData - The user data to store
 */
export const createUser = async (
  userId: string,
  userData: CustomUserFields
) => {
  try {
    // Add server timestamp for creation time
    const userDataWithTimestamp = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Create the user document with the user's UID as document ID
    await setDoc(doc(db, USERS_COLLECTION, userId), userDataWithTimestamp);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Gets a user document from Firestore
 * @param userId - The user's UID
 * @returns The user document data or null if not found
 */
export const getUser = async (
  userId: string
): Promise<CustomUserFields | null> => {
  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    let userDoc;

    try {
      userDoc = await getDoc(userDocRef);
    } catch (error) {
      // If error indicates we're offline but navigator says we're online, try reconnecting
      if (
        error instanceof Error &&
        error.message.includes("client is offline") &&
        navigator.onLine
      ) {
        await reconnectFirestore();
        // Try again after reconnection
        userDoc = await getDoc(userDocRef);
      } else {
        // If it's another type of error, rethrow
        throw error;
      }
    }

    if (userDoc.exists()) {
      const userData = userDoc.data() as DocumentData;

      // Ensure ProfileVisibility is properly typed
      let profileVisibility = ProfileVisibility.Public;
      if (
        userData.profileVisibility &&
        Object.values(ProfileVisibility).includes(userData.profileVisibility)
      ) {
        profileVisibility = userData.profileVisibility as ProfileVisibility;
      }

      let directoryVisibility = ProfileVisibility.Public;
      if (
        userData.directoryVisibility &&
        Object.values(ProfileVisibility).includes(userData.directoryVisibility)
      ) {
        directoryVisibility = userData.directoryVisibility as ProfileVisibility;
      }

      // Create properly typed user object
      const user: CustomUserFields = {
        userId: userData.userId || userId,
        email: userData.email || "",
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        handle: userData.handle || "",
        location: userData.location || "",
        affiliations: userData.affiliations || null,
        profilePicture: userData.profilePicture || null,
        bio: userData.bio || null,
        dateOfBirth: userData.dateOfBirth || null,
        joinedDate: userData.joinedDate || new Date("2023-01-01"), // Platform launch date fallback
        followersCount: userData.followersCount || 0,
        followingCount: userData.followingCount || 0,
        postsCount: userData.postsCount || 0,
        likesGiven: userData.likesGiven || 0,
        commentsCount: userData.commentsCount || 0,
        communitiesJoined: userData.communitiesJoined || [],
        language: userData.language || "en",
        theme: userData.theme || "light",
        notificationsEnabled: userData.notificationsEnabled !== false,
        interests: userData.interests || [],
        isProfilePrivate: userData.isProfilePrivate || false,
        profileVisibility: profileVisibility,
        directoryVisibility: directoryVisibility,
        affiliationsVisibilitySettings:
          userData.affiliationsVisibilitySettings || [],
        blockedUsers: userData.blockedUsers || [],
        accountStatus: userData.accountStatus || "active",
        lastLogin: userData.lastLogin || new Date(),
        verified: userData.verified || false,
        passwordResetRequired: userData.passwordResetRequired || false,
        phoneNumber: userData.phoneNumber || null,
        secondaryEmail: userData.secondaryEmail || null,
        lastPostDate: userData.lastPostDate || null,
        recentSearches: userData.recentSearches || [],
        devices: userData.devices || [],
        loginMetrics: userData.loginMetrics || 0,
        viewsMetrics: userData.viewsMetrics || 0,
        likesMetrics: userData.likesMetrics || 0,
        commentsMetrics: userData.commentsMetrics || 0,
        jobTitle: userData.jobTitle || "",
        quote: userData.quote || "",
        graduationYear: userData.graduationYear || undefined,
        house: userData.house || undefined,
        dormitory: userData.dormitory || undefined,
        isStaff: userData.isStaff || false,
        isCommunityAdmin: userData.isCommunityAdmin || [],
        institutionalAdmin: userData.institutionalAdmin || [],
        isTrustee: userData.isTrustee || [],
        hasCompletedOnboarding: userData.hasCompletedOnboarding || false,
        agreedToTermsAt: userData.agreedToTermsAt || null,
        agreedToGuidelinesAt: userData.agreedToGuidelinesAt || null,
        emailVerified: userData.emailVerified || false,
        subscription: userData.subscription || SubscriptionTier.Basic,
        subscriptionStartDate: userData.subscriptionStartDate || new Date(),
        subscriptionEndDate: userData.subscriptionEndDate || null,
        pausedUntil: userData.pausedUntil || null,
        agreedToPrivacyAt: userData.agreedToPrivacyAt || null,
        mainAppTOSAcknowledgedAt: userData.mainAppTOSAcknowledgedAt || null,
        mainAppPrivacyAcknowledgedAt:
          userData.mainAppPrivacyAcknowledgedAt || null,
        mainAppGuidelinesAcknowledgedAt:
          userData.mainAppGuidelinesAcknowledgedAt || null,
        whitelabelTOSAcknowledgedAt:
          userData.whitelabelTOSAcknowledgedAt || null,
        whitelabelPrivacyAcknowledgedAt:
          userData.whitelabelPrivacyAcknowledgedAt || null,
        whitelabelGuidelinesAcknowledgedAt:
          userData.whitelabelGuidelinesAcknowledgedAt || null,
        lastLegalDocumentVersion: userData.lastLegalDocumentVersion || null,
      };

      return user;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Updates an existing user document in Firestore
 * @param userId - The user's UID
 * @param userData - The partial user data to update
 */
export const updateUser = async (
  userId: string,
  userData: Partial<CustomUserFields>
) => {
  try {
    const userDataWithTimestamp = {
      ...userData,
      updatedAt: serverTimestamp(),
    };

    const userDocRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userDocRef, userDataWithTimestamp);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Gets a user by handle
 * @param handle - The user's handle
 * @returns The user document or null if not found
 */
export const getUserByHandle = async (
  handle: string
): Promise<CustomUserFields | null> => {
  try {
    // Handles are stored in lowercase
    const handleRef = doc(db, `handles/${handle.toLowerCase()}`);
    const handleDoc = await getDoc(handleRef);

    if (!handleDoc.exists()) {
      // Handle not found, return null without logging
      return null;
    }

    // Get the userId from the handle document and use it to get the full user
    const userId = handleDoc.data().userId;
    const user = await getUserById(userId);
    return user;
  } catch (error) {
    throw error;
  }
};

// Update user profile in Firestore. If a new handle is provided,
// attempt to create a new handle document. If that fails because the
// handle already exists, return an error. Otherwise, delete the old handle document
// (if any) and update the user's document.
export const updateUserProfile = async (
  userId: string,
  updatedFields: Partial<CustomUserFields>
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnapshot = await getDoc(userRef);

    if (!userSnapshot.exists()) {
      return { success: false, error: "User not found" };
    }

    const user = { ...userSnapshot.data(), userId } as CustomUserFields;

    // First update the user document
    try {
      // Remove handle from fields to update (we'll handle that separately)
      const { handle, ...fieldsToUpdate } = updatedFields;

      await updateDoc(userRef, fieldsToUpdate);
      // Handle changes to the user's handle separately
      if (
        updatedFields.handle &&
        updatedFields.handle !== user.handle &&
        updatedFields.handle.trim() !== ""
      ) {
        try {
          // Directly try to create the new handle document
          // If it already exists, Firebase will throw an error
          const newHandleRef = doc(
            db,
            HANDLE_COLLECTION,
            updatedFields.handle.toLowerCase()
          );

          // Use setDoc with merge: false to fail if document already exists
          await setDoc(
            newHandleRef,
            {
              userId: user.userId,
              handle: updatedFields.handle.toLowerCase(),
              createdAt: serverTimestamp(),
            },
            { merge: false }
          );

          // If we got here, the handle was successfully created
          // Now update the user document with the new handle
          await updateDoc(userRef, { handle: updatedFields.handle });

          // Now try to delete the old handle document if it exists and is valid
          if (
            user.handle &&
            user.handle !== "Not set" &&
            user.handle.trim() !== ""
          ) {
            try {
              const oldHandleRef = doc(
                db,
                HANDLE_COLLECTION,
                user.handle.toLowerCase()
              );
              await deleteDoc(oldHandleRef);
            } catch (deleteError: any) {
              // If we can't delete the old handle, continue
              // The profile and new handle update already succeeded
              if (deleteError.code === "permission-denied") {
                return {
                  success: true,
                  message:
                    "Profile updated, but your old handle couldn't be removed completely.",
                };
              }
            }
          }

          return { success: true, message: "Profile updated successfully" };
        } catch (handleError: any) {
          // Handle specific Firebase errors
          if (handleError.code === "permission-denied") {
            return {
              success: false,
              error:
                "Permission denied when updating your handle. Your profile was updated, but your handle remains unchanged.",
            };
          }

          // This is the key error code for document already exists
          if (
            handleError.code === "already-exists" ||
            handleError.message?.includes("Document already exists")
          ) {
            return {
              success: false,
              error: "This handle is already taken. Please choose another one.",
            };
          }

          // For other errors, return a generic error message
          return {
            success: false,
            error:
              "Failed to update your handle. Your profile was updated, but your handle remains unchanged. Error: " +
              handleError.message,
          };
        }
      }

      return { success: true, message: "Profile updated successfully" };
    } catch (error: any) {
      // General error handler
      if (error.code === "permission-denied") {
        return {
          success: false,
          error: "Permission denied. Please try logging out and back in.",
        };
      }
      return {
        success: false,
        error: "An unexpected error occurred: " + error.message,
      };
    }
  } catch (error: any) {
    // General error handler
    if (error.code === "permission-denied") {
      return {
        success: false,
        error: "Permission denied. Please try logging out and back in.",
      };
    }
    return {
      success: false,
      error: "An unexpected error occurred: " + error.message,
    };
  }
};

// Add a new function to ensure handle consistency
export const ensureHandleConsistency = async (
  userId: string
): Promise<void> => {
  try {
    // Get the user's data
    const userData = await getUser(userId);
    if (!userData || !userData.handle) return;

    // Check if the handle document exists and points to this user
    const handleDocRef = doc(
      db,
      HANDLE_COLLECTION,
      userData.handle.toLowerCase()
    );
    const handleDocSnap = await getDoc(handleDocRef);

    if (!handleDocSnap.exists()) {
      // Handle document doesn't exist, create it
      await setDoc(handleDocRef, { userId: userId });
    } else {
      const handleData = handleDocSnap.data();
      if (handleData.userId !== userId) {
        // You could choose to reset this user's handle or take other action here
      }
    }
  } catch (error) {
    throw error;
  }
};

// Background process to check handle consistency for all users
// This should be run periodically to ensure data integrity
export const batchEnsureHandleConsistency = async (): Promise<void> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const usersSnapshot = await getDocs(usersRef);

    // Process all users in parallel
    await Promise.all(
      (
        await usersSnapshot.docs
      ).map((doc) => {
        const userId = doc.id;
        return ensureHandleConsistency(userId);
      })
    );

    // Simply track errors, don't log
  } catch (err) {
    // Don't log the error
  }
};

// Cleanup function to remove invalid "Not set" handles from the database
export const cleanupInvalidHandles = async (): Promise<{
  success: boolean;
  cleaned: number;
  message: string;
}> => {
  try {
    let cleanedCount = 0;

    // Check for "Not set" handle documents
    const invalidHandles = ["not set", "Not set", "NOT SET", ""];

    for (const invalidHandle of invalidHandles) {
      if (invalidHandle.trim() === "") continue;

      try {
        const handleRef = doc(
          db,
          HANDLE_COLLECTION,
          invalidHandle.toLowerCase()
        );
        const handleDoc = await getDoc(handleRef);

        if (handleDoc.exists()) {
          await deleteDoc(handleRef);
          cleanedCount++;
        }
      } catch (error) {
        // Continue with other handles if one fails
      }
    }

    // Also clean up users who have "Not set" as their handle field
    const usersRef = collection(db, USERS_COLLECTION);
    const usersQuery = query(
      usersRef,
      where("handle", "in", ["not set", "Not set", "NOT SET"])
    );
    const usersSnapshot = await getDocs(usersQuery);

    const userUpdates = usersSnapshot.docs.map(async (userDoc) => {
      try {
        await updateDoc(userDoc.ref, { handle: "" });
        cleanedCount++;
      } catch (error) {
      }
    });

    await Promise.all(userUpdates);

    return {
      success: true,
      cleaned: cleanedCount,
      message: `Successfully cleaned ${cleanedCount} invalid handle entries`,
    };
  } catch (error: any) {
    return {
      success: false,
      cleaned: 0,
      message: `Failed to cleanup invalid handles: ${error.message}`,
    };
  }
};

// Enhanced getUser function to ensure handle consistency
export const getUserEnhanced = async (
  userId: string
): Promise<CustomUserFields | null> => {
  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    const snapshot = await getDoc(userDocRef);

    if (!snapshot.exists()) {
      return null;
    }

    const userData = snapshot.data() as CustomUserFields;

    // Check handle consistency in the background (don't await)
    if (userData.handle) {
      ensureHandleConsistency(userId).catch(() => {
        // Silently handle error
      });
    }

    return userData;
  } catch (error) {
    throw error;
  }
};

// Retrieve all users with pagination and filters
export const getUsers = async (
  page = 1,
  pageSize = 10,
  filters: {
    searchQuery?: string;
    affiliationId?: string;
    sortBy?: string;
    includePrivate?: boolean;
    excludeUserIds?: string[];
    suggestionsForUser?: string;
    includeOnlyUserIds?: string[];
    industryFilter?: string;
  } = {}
): Promise<{
  users: {
    userId: string;
    handle: string;
    postsCount: number;
    followersCount: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    jobTitle?: string;
    affiliations?: string[];
    joinedDate?: Date;
    profilePicture?: string;
    bio?: string;
    location?: string;
    verified?: boolean;
  }[];
  totalCount: number;
}> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Check if current user is staff
    let isStaff = false;
    if (currentUser) {
      try {
        const staffService = await import("./staff.service");
        isStaff = await staffService.isUserStaff(currentUser.uid);
      } catch (error) {
        console.error("Error checking staff status:", error);
      }
    }

    const usersRef = collection(db, USERS_COLLECTION);

    // Build query based on user permissions
    let q;
    if (isStaff && filters.includePrivate) {
      // Staff can query all users without restrictions
      q = query(usersRef);
    } else {
      // Regular users and staff requesting public-only must filter by isProfilePrivate == false
      // This is required by the Firestore security rules
      q = query(usersRef, where("isProfilePrivate", "==", false));
    }

    // Apply affiliation filter if provided
    if (filters.affiliationId) {
      // This requires a more complex query - we'll need to get the users first and filter later
      const snapshot = await getDocs(q);

      // Get all users that match the visibility criteria
      let filteredUsers = snapshot.docs.map((doc) => ({
        userId: doc.id,
        handle: doc.data().handle || "Unknown",
        postsCount: doc.data().postsCount || 0,
        followersCount: doc.data().followersCount || 0,
        firstName: doc.data().firstName || "",
        lastName: doc.data().lastName || "",
        email: doc.data().email || "",
        jobTitle: doc.data().jobTitle || "",
        affiliations: doc.data().affiliations || [],
        joinedDate: doc.data().joinedDate,
        profilePicture: doc.data().profilePicture || null,
        bio: doc.data().bio || null,
        location: doc.data().location || "",
        verified: doc.data().verified || false,
      }));

      // Filter by affiliation
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.affiliations &&
          user.affiliations.includes(filters.affiliationId!)
      );

      // Apply industry filter if provided
      if (filters.industryFilter && filters.industryFilter !== "all") {
        filteredUsers = filteredUsers.filter((user) =>
          user.jobTitle?.includes(filters.industryFilter!)
        );
      }

      // Filter out excluded user IDs
      if (filters.excludeUserIds && filters.excludeUserIds.length > 0) {
        filteredUsers = filteredUsers.filter(
          (user) => !filters.excludeUserIds?.includes(user.userId)
        );
      }

      // Filter to include only specified user IDs
      if (filters.includeOnlyUserIds && filters.includeOnlyUserIds.length > 0) {
        filteredUsers = filteredUsers.filter((user) =>
          filters.includeOnlyUserIds?.includes(user.userId)
        );
      }

      // Apply search filtering if needed
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.firstName?.toLowerCase().includes(searchLower) ||
            user.lastName?.toLowerCase().includes(searchLower) ||
            user.handle?.toLowerCase().includes(searchLower) ||
            user.location?.toLowerCase().includes(searchLower) ||
            user.jobTitle?.toLowerCase().includes(searchLower)
        );
      }

      // Apply sorting
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "nameAsc":
            filteredUsers.sort((a, b) =>
              `${a.firstName} ${a.lastName}`.localeCompare(
                `${b.firstName} ${b.lastName}`
              )
            );
            break;
          case "nameDesc":
            filteredUsers.sort((a, b) =>
              `${b.firstName} ${b.lastName}`.localeCompare(
                `${a.firstName} ${a.lastName}`
              )
            );
            break;
          case "recent":
            filteredUsers.sort((a, b) => {
              const dateA = a.joinedDate ? new Date(a.joinedDate).getTime() : 0;
              const dateB = b.joinedDate ? new Date(b.joinedDate).getTime() : 0;
              return dateB - dateA;
            });
            break;
          case "followersDesc":
            filteredUsers.sort(
              (a, b) => (b.followersCount || 0) - (a.followersCount || 0)
            );
            break;
          default:
            break;
        }
      }

      // Calculate pagination variables
      const totalCount = filteredUsers.length;
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      // Return paginated results
      return {
        users: filteredUsers.slice(startIndex, endIndex),
        totalCount,
      };
    } else {
      // If no affiliation filter, we can get all users at once and filter client-side
      const snapshot = await getDocs(q);

      // Map documents to user objects
      let allUsers = snapshot.docs.map((doc) => ({
        userId: doc.id,
        handle: doc.data().handle || "Unknown",
        postsCount: doc.data().postsCount || 0,
        followersCount: doc.data().followersCount || 0,
        firstName: doc.data().firstName || "",
        lastName: doc.data().lastName || "",
        email: doc.data().email || "",
        jobTitle: doc.data().jobTitle || "",
        affiliations: doc.data().affiliations || [],
        joinedDate: doc.data().joinedDate,
        profilePicture: doc.data().profilePicture || null,
        bio: doc.data().bio || null,
        location: doc.data().location || "",
        verified: doc.data().verified || false,
      }));

      // Apply industry filter if provided
      if (filters.industryFilter && filters.industryFilter !== "all") {
        allUsers = allUsers.filter((user) =>
          user.jobTitle?.includes(filters.industryFilter!)
        );
      }

      // Filter out excluded user IDs
      if (filters.excludeUserIds && filters.excludeUserIds.length > 0) {
        allUsers = allUsers.filter(
          (user) => !filters.excludeUserIds?.includes(user.userId)
        );
      }

      // Filter to include only specified user IDs
      if (filters.includeOnlyUserIds && filters.includeOnlyUserIds.length > 0) {
        allUsers = allUsers.filter((user) =>
          filters.includeOnlyUserIds?.includes(user.userId)
        );
      }

      // Apply text search if provided
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        allUsers = allUsers.filter(
          (user) =>
            user.firstName?.toLowerCase().includes(searchLower) ||
            user.lastName?.toLowerCase().includes(searchLower) ||
            user.handle?.toLowerCase().includes(searchLower) ||
            user.location?.toLowerCase().includes(searchLower) ||
            user.jobTitle?.toLowerCase().includes(searchLower)
        );
      }

      // Apply sorting
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "nameAsc":
            allUsers.sort((a, b) =>
              `${a.firstName} ${a.lastName}`.localeCompare(
                `${b.firstName} ${b.lastName}`
              )
            );
            break;
          case "nameDesc":
            allUsers.sort((a, b) =>
              `${b.firstName} ${b.lastName}`.localeCompare(
                `${a.firstName} ${a.lastName}`
              )
            );
            break;
          case "recent":
            allUsers.sort((a, b) => {
              const dateA = a.joinedDate ? new Date(a.joinedDate).getTime() : 0;
              const dateB = b.joinedDate ? new Date(b.joinedDate).getTime() : 0;
              return dateB - dateA;
            });
            break;
          case "followersDesc":
            allUsers.sort(
              (a, b) => (b.followersCount || 0) - (a.followersCount || 0)
            );
            break;
          default:
            break;
        }
      }

      // Calculate pagination variables
      const totalCount = allUsers.length;
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      // Return paginated results
      return {
        users: allUsers.slice(startIndex, endIndex),
        totalCount,
      };
    }
  } catch (error) {
    // Return empty result on error, no logging needed
    return { users: [], totalCount: 0 };
  }
};

// Get handle data
export const getHandle = async (handle: string) => {
  try {
    const handleDocRef = doc(db, HANDLE_COLLECTION, handle);
    const handleDoc = await getDoc(handleDocRef);
    if (handleDoc.exists()) {
      return handleDoc.data();
    }
    return null;
  } catch (error) {
    throw new Error("Failed to check handle uniqueness.");
  }
};

// Update user settings (notifications and privacy)
export const updateUserSettings = async (
  userId: string,
  settings: {
    notificationsEnabled: boolean;
    isProfilePrivate: boolean;
  }
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userDocRef, settings);
    return { success: true, message: "Settings updated successfully" };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get user by email address
 * @param email - The email address to search for
 * @returns A promise resolving to the user object or null if not found
 */
export const getUserByEmail = async (email: string) => {
  try {
    // Query the users collection by email
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where("email", "==", email.toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const userDoc = querySnapshot.docs[0];
    return {
      userId: userDoc.id,
      ...userDoc.data(),
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get user by ID (simple version without handle consistency check)
 * @param userId - The user ID to fetch
 * @returns A promise resolving to the user object or null if not found
 */
export const getUserById = async (
  userId: string
): Promise<CustomUserFields | null> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnapshot = await getDoc(userRef);

    if (!userSnapshot.exists()) {
      return null;
    }

    const userData = userSnapshot.data() as DocumentData;

    // Ensure ProfileVisibility is properly typed
    let profileVisibility = ProfileVisibility.Public;
    if (
      userData.profileVisibility &&
      Object.values(ProfileVisibility).includes(userData.profileVisibility)
    ) {
      profileVisibility = userData.profileVisibility as ProfileVisibility;
    }

    let directoryVisibility = ProfileVisibility.Public;
    if (
      userData.directoryVisibility &&
      Object.values(ProfileVisibility).includes(userData.directoryVisibility)
    ) {
      directoryVisibility = userData.directoryVisibility as ProfileVisibility;
    }

    // Create properly typed user object
    const user: CustomUserFields = {
      userId: userData.userId || userSnapshot.id,
      email: userData.email || "",
      firstName: userData.firstName || "",
      lastName: userData.lastName || "",
      handle: userData.handle || "",
      location: userData.location || "",
      affiliations: userData.affiliations || null,
      profilePicture: userData.profilePicture || null,
      bio: userData.bio || null,
      dateOfBirth: userData.dateOfBirth || null,
      joinedDate: userData.joinedDate || new Date("2023-01-01"), // Platform launch date fallback
      followersCount: userData.followersCount || 0,
      followingCount: userData.followingCount || 0,
      postsCount: userData.postsCount || 0,
      likesGiven: userData.likesGiven || 0,
      commentsCount: userData.commentsCount || 0,
      communitiesJoined: userData.communitiesJoined || [],
      language: userData.language || "en",
      theme: userData.theme || "light",
      notificationsEnabled: userData.notificationsEnabled !== false,
      interests: userData.interests || [],
      isProfilePrivate: userData.isProfilePrivate || false,
      profileVisibility: profileVisibility,
      directoryVisibility: directoryVisibility,
      affiliationsVisibilitySettings:
        userData.affiliationsVisibilitySettings || [],
      blockedUsers: userData.blockedUsers || [],
      accountStatus: userData.accountStatus || "active",
      lastLogin: userData.lastLogin || new Date(),
      verified: userData.verified || false,
      passwordResetRequired: userData.passwordResetRequired || false,
      hasCompletedOnboarding: userData.hasCompletedOnboarding || false,
      phoneNumber: userData.phoneNumber || null,
      secondaryEmail: userData.secondaryEmail || null,
      lastPostDate: userData.lastPostDate || null,
      recentSearches: userData.recentSearches || [],
      devices: userData.devices || [],
      loginMetrics: userData.loginMetrics || 0,
      viewsMetrics: userData.viewsMetrics || 0,
      likesMetrics: userData.likesMetrics || 0,
      commentsMetrics: userData.commentsMetrics || 0,
      jobTitle: userData.jobTitle || "",
      quote: userData.quote || "",
      isStaff: userData.isStaff || false,
      isCommunityAdmin: userData.isCommunityAdmin || [],
      institutionalAdmin: userData.institutionalAdmin || [],
      isTrustee: userData.isTrustee || [],
      agreedToTermsAt: userData.agreedToTermsAt || null,
      subscription: userData.subscription || SubscriptionTier.Basic,
      subscriptionStartDate: userData.subscriptionStartDate || new Date(),
      subscriptionEndDate: userData.subscriptionEndDate || null,
      pausedUntil: userData.pausedUntil || null,
      agreedToGuidelinesAt: userData.agreedToGuidelinesAt || null,
      emailVerified: userData.emailVerified || false,
      agreedToPrivacyAt: userData.agreedToPrivacyAt || null,
      mainAppTOSAcknowledgedAt: userData.mainAppTOSAcknowledgedAt || null,
      mainAppPrivacyAcknowledgedAt:
        userData.mainAppPrivacyAcknowledgedAt || null,
      mainAppGuidelinesAcknowledgedAt:
        userData.mainAppGuidelinesAcknowledgedAt || null,
      whitelabelTOSAcknowledgedAt: userData.whitelabelTOSAcknowledgedAt || null,
      whitelabelPrivacyAcknowledgedAt:
        userData.whitelabelPrivacyAcknowledgedAt || null,
      whitelabelGuidelinesAcknowledgedAt:
        userData.whitelabelGuidelinesAcknowledgedAt || null,
      lastLegalDocumentVersion: userData.lastLegalDocumentVersion || null,
    };

    return user;
  } catch (error) {
    throw error;
  }
};

/**
 * Creates a user document in Firestore
 * @param userId - The user's UID
 * @param userData - The user data to store
 */
export const createUserDocument = async (
  userId: string,
  userData: CustomUserFields
) => {
  try {
    // Add server timestamp for creation time
    const userDataWithTimestamp = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Create the user document with the user's UID as document ID
    await setDoc(doc(db, USERS_COLLECTION, userId), userDataWithTimestamp);
    return true;
  } catch (error) {
    throw error;
  }
};

export const ensureHandleDocument = async (
  handle: string,
  userId: string
): Promise<boolean> => {
  try {
    if (!handle || handle.trim() === "") {
      return false;
    }

    // Normalize the handle
    const normalizedHandle = handle.toLowerCase().trim();

    // Create handle document in the handles collection
    const handleRef = doc(db, HANDLE_COLLECTION, normalizedHandle);
    const handleDoc = await getDoc(handleRef);

    if (handleDoc.exists()) {
      // Handle already exists
      const existingUserId = handleDoc.data().userId;
      if (existingUserId === userId) {
        // Handle already exists and belongs to this user
        return true;
      } else {
        // Handle already taken by a different user
        return false;
      }
    }

    // Create a new handle document
    await setDoc(handleRef, {
      userId,
      handle: normalizedHandle,
      createdAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Initializes the handles collection by creating handle documents for all users
 * who have a handle in their user document but no corresponding entry in the handles collection.
 *
 * Call this function during application initialization to ensure the handles collection exists
 * and contains all necessary documents.
 */
export const initializeHandlesCollection = async (): Promise<{
  success: boolean;
  created: number;
  failed: number;
  message: string;
}> => {
  try {
    // Get all users that have a handle field
    const usersRef = collection(db, USERS_COLLECTION);
    const usersSnapshot = await getDocs(usersRef);

    let created = 0;
    let failed = 0;

    // Process each user document
    const results = await Promise.allSettled(
      usersSnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const userId = userDoc.id;

        // If this user has a handle, ensure a handle document exists
        if (userData.handle && userData.handle.trim() !== "") {
          const handleLowercase = userData.handle.toLowerCase();
          const handleRef = doc(db, HANDLE_COLLECTION, handleLowercase);

          // Check if the handle document already exists
          const handleDoc = await getDoc(handleRef);

          if (!handleDoc.exists()) {
            // Create the handle document
            await setDoc(handleRef, {
              userId: userId,
              handle: handleLowercase,
              createdAt: serverTimestamp(),
            });
            return { success: true, skipped: false };
          }
        }
        return { success: true, skipped: true };
      })
    );

    // Count successes and failures
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        // Add proper type assertion to handle promise result
        const value = result.value as { success: boolean; skipped?: boolean };
        if (value.success && !value.skipped) {
          created++;
        }
      } else if (result.status === "rejected") {
        failed++;
      }
    });

    return {
      success: true,
      created,
      failed,
      message: `Handles collection initialized: ${created} handle documents created, ${failed} failed`,
    };
  } catch (error: any) {
    return {
      success: false,
      created: 0,
      failed: 0,
      message: `Failed to initialize handles collection: ${error.message}`,
    };
  }
};

// Call this function on app initialization to verify the handles collection
export const ensureHandlesCollectionExists = async (): Promise<void> => {
  try {
    // Try to get one document from handles collection
    const handleQuery = query(collection(db, HANDLE_COLLECTION), limit(1));
    const handleSnapshot = await getDocs(handleQuery);

    // If the collection doesn't exist or is empty, initialize it
    if (handleSnapshot.empty) {
      // Collection doesn't exist or is empty, initialize it
      await initializeHandlesCollection();
    }
  } catch (error) {
    // Silently fail, no logging needed
  }
};

/**
 * Test function to diagnose handle creation permission issues.
 * This function attempts to create a handle document for a user,
 * with detailed error reporting.
 */
export const testHandleCreation = async (
  userId: string,
  handleToCreate: string
): Promise<{ success: boolean; message: string; error?: any }> => {
  try {
    // Check auth state
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return {
        success: false,
        message: "Not authenticated",
        error: "User not logged in",
      };
    }

    // Check if handle document already exists
    const handleRef = doc(db, HANDLE_COLLECTION, handleToCreate.toLowerCase());
    const handleSnapshot = await getDoc(handleRef);

    if (handleSnapshot.exists()) {
      const handleData = handleSnapshot.data();

      return {
        success: false,
        message: "Handle already exists",
        error: { handleData },
      };
    }

    // Try to create the handle document
    await setDoc(handleRef, {
      userId: userId,
      handle: handleToCreate.toLowerCase(),
      createdAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Handle created successfully",
    };
  } catch (error) {
    return {
      success: false,
      message: "Error creating handle",
      error,
    };
  }
};

export const debugCreateHandle = async (handle: string): Promise<any> => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return { error: "Not authenticated" };
    }

    try {
      // Try to create the handle
      await setDoc(doc(db, HANDLE_COLLECTION, handle.toLowerCase()), {
        userId: user.uid,
        handle: handle.toLowerCase(),
        createdAt: serverTimestamp(),
      });

      return { success: true, message: "Handle created successfully" };
    } catch (error) {
      return { error: "Error creating handle", details: error };
    }
  } catch (e) {
    return { error: "Unexpected error", details: e };
  }
};

if (import.meta.env.DEV) {
  (window as any).debugCreateHandle = debugCreateHandle;
}

/**
 * Search for users by name, email, or handle
 * Staff can search all users, regular users can only search connections
 * @param searchTerm - The search term to find users
 * @param exactMatch - Whether to search for an exact match (useful for userId searches)
 * @param isStaffSearch - Whether this is a staff-initiated search (allows searching all users)
 * @returns Array of user objects matching the search criteria
 */
export const searchUsers = async (
  searchTerm: string,
  exactMatch: boolean = false,
  isStaffSearch: boolean = false
): Promise<Array<CustomUserFields>> => {
  try {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    // Get current user
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found");
      return [];
    }

    // Check if user is staff if staff search is requested
    let isStaff = false;
    if (isStaffSearch) {
      const idTokenResult = await currentUser.getIdTokenResult();
      isStaff =
        idTokenResult?.claims?.staff === true ||
        idTokenResult?.claims?.admin === true;
      if (!isStaff) {
        isStaffSearch = false;
      }
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    // If staff search, search all users
    if (isStaffSearch) {
      const usersRef = collection(db, USERS_COLLECTION);
      const searchResults: CustomUserFields[] = [];

      // Get all users and filter client-side (not efficient but works for small datasets)
      const snapshot = await getDocs(query(usersRef, limit(100)));

      snapshot.forEach((doc) => {
        const userData = doc.data() as DocumentData;
        const fullName = `${userData.firstName || ""} ${
          userData.lastName || ""
        }`.toLowerCase();
        const handle = (userData.handle || "").toLowerCase();
        const email = (userData.email || "").toLowerCase();

        // Use exactMatch parameter to determine matching approach
        if (exactMatch) {
          // For exact matches, check for equality
          if (
            fullName === lowerSearchTerm ||
            handle === lowerSearchTerm ||
            email === lowerSearchTerm
          ) {
            searchResults.push({
              ...userData,
              userId: doc.id,
            } as CustomUserFields);
          }
        } else {
          // For partial matches, check for inclusion
          if (
            fullName.includes(lowerSearchTerm) ||
            handle.includes(lowerSearchTerm) ||
            email.includes(lowerSearchTerm)
          ) {
            searchResults.push({
              ...userData,
              userId: doc.id,
            } as CustomUserFields);
          }
        }
      });

      // Sort results by relevance
      return sortSearchResults(searchResults, lowerSearchTerm);
    }

    // For regular users, connections functionality has been removed
    // Only staff can search users
    return [];
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
};

/**
 * Helper function to sort search results by relevance
 */
const sortSearchResults = (
  results: CustomUserFields[],
  searchTerm: string
): CustomUserFields[] => {
  return results.sort((a, b) => {
    const aName = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
    const bName = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();

    // Exact name matches first
    if (aName === searchTerm && bName !== searchTerm) return -1;
    if (bName === searchTerm && aName !== searchTerm) return 1;

    // Then handle matches
    if (
      (a.handle || "").toLowerCase() === searchTerm &&
      (b.handle || "").toLowerCase() !== searchTerm
    )
      return -1;
    if (
      (b.handle || "").toLowerCase() === searchTerm &&
      (a.handle || "").toLowerCase() !== searchTerm
    )
      return 1;

    // Then alphabetically by name
    return aName.localeCompare(bName);
  });
};
